import TableChartIcon from '@mui/icons-material/TableChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import { FC, memo, ReactNode, useCallback, useState } from 'react';
import { TariffCalculationParams } from 'src/hooks/useTariffCalculation.mutation';

type Props = {
  dialogIsOpen: boolean;
  setDialogIsOpen: (open: boolean) => void;
  /** 計算対象の shift_truck_id（= delivery.id）*/
  selectedDeliveryIds: number[];
  /** 対象の説明文。未指定なら選択画面向けの既定文（V3Plans / V3Timeline の現行文言）*/
  targetDescription?: ReactNode;
  onExecute: (params: TariffCalculationParams) => void;
  isExecuting?: boolean;
};

const TariffCalculationDialog: FC<Props> = memo(({
  dialogIsOpen,
  setDialogIsOpen,
  selectedDeliveryIds,
  targetDescription,
  onExecute,
  isExecuting = false,
}) => {
  const [calculateBilling, setCalculateBilling] = useState(true);
  const [calculatePayment, setCalculatePayment] = useState(true);
  const [skipExistingAmounts, setSkipExistingAmounts] = useState(true);

  const handleCancel = useCallback(() => {
    setDialogIsOpen(false);
  }, [setDialogIsOpen]);

  const handleExecute = useCallback(() => {
    onExecute({
      shiftTruckIds: selectedDeliveryIds,
      calculateBilling,
      calculatePayment,
      skipExistingAmounts,
    });
  }, [onExecute, selectedDeliveryIds, calculateBilling, calculatePayment, skipExistingAmounts]);

  if (!dialogIsOpen) {
    return null;
  }

  return (
    <Dialog
      open={dialogIsOpen}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <TableChartIcon />
          <Typography variant="h6">運賃計算の実行</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Box
            sx={{
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
            }}
          >
            <Typography>
              {targetDescription ?? (
                <>選択中の <strong>{selectedDeliveryIds.length}件</strong> のルートに対して計算を実行します。</>
              )}
            </Typography>
          </Box>

          <Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>計算対象</Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={calculateBilling}
                    onChange={(e) => setCalculateBilling(e.target.checked)}
                  />
                }
                label="請求(売上)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={calculatePayment}
                    onChange={(e) => setCalculatePayment(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography component="span">支払い(原価)</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ※計算結果は詳細画面にのみ反映されます
                    </Typography>
                  </Box>
                }
              />
            </FormGroup>
          </Box>

          <Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>上書き設定</Typography>
            <Box
              sx={{
                ml: 1,
                p: 2,
                bgcolor: 'warning.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main'
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipExistingAmounts}
                    onChange={(e) => setSkipExistingAmounts(e.target.checked)}
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <WarningAmberIcon color="warning" fontSize="small" />
                    <Typography>入力済みの金額はスキップする（上書きしない）</Typography>
                  </Stack>
                }
              />
            </Box>
          </Box>

        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="info" variant="outlined" disabled={isExecuting}>
          キャンセル
        </Button>
        <Button
          onClick={handleExecute}
          variant="contained"
          disabled={!calculateBilling && !calculatePayment || selectedDeliveryIds.length === 0 || isExecuting}
        >
          {isExecuting ? '実行中...' : '実行する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default TariffCalculationDialog;
